/**
 * Utility to parse Pokémon Showdown export format
 */

const NATURES = {
  hardy: { boost: null, drop: null },
  lonely: { boost: 'attack', drop: 'defense' },
  brave: { boost: 'attack', drop: 'speed' },
  adamant: { boost: 'attack', drop: 'special-attack' },
  naughty: { boost: 'attack', drop: 'special-defense' },
  bold: { boost: 'defense', drop: 'attack' },
  docile: { boost: null, drop: null },
  relaxed: { boost: 'defense', drop: 'speed' },
  impish: { boost: 'defense', drop: 'special-attack' },
  lax: { boost: 'defense', drop: 'special-defense' },
  timid: { boost: 'speed', drop: 'attack' },
  hasty: { boost: 'speed', drop: 'defense' },
  serious: { boost: null, drop: null },
  jolly: { boost: 'speed', drop: 'special-attack' },
  naive: { boost: 'speed', drop: 'special-defense' },
  modest: { boost: 'special-attack', drop: 'attack' },
  mild: { boost: 'special-attack', drop: 'defense' },
  quiet: { boost: 'special-attack', drop: 'speed' },
  bashful: { boost: null, drop: null },
  rash: { boost: 'special-attack', drop: 'special-defense' },
  calm: { boost: 'special-defense', drop: 'attack' },
  gentle: { boost: 'special-defense', drop: 'defense' },
  sassy: { boost: 'special-defense', drop: 'speed' },
  careful: { boost: 'special-defense', drop: 'special-attack' },
  quirky: { boost: null, drop: null }
};

export const parseShowdownFormat = (text) => {
  const pokemon = [];
  const blocks = text.split(/\n\n+/).filter(b => b.trim());
  
  for (const block of blocks) {
    const lines = block.split('\n').map(l => l.trim()).filter(l => l);
    if (lines.length === 0) continue;
    
    const poke = {
      name: '',
      nickname: '',
      item: '',
      ability: '',
      level: 100,
      shiny: false,
      gender: 'random',
      nature: 'hardy',
      teraType: '',
      evs: { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 },
      ivs: { hp: 31, atk: 31, def: 31, spa: 31, spd: 31, spe: 31 },
      moves: []
    };
    
    // First line: Name (Nickname) @ Item or just Name @ Item
    const firstLine = lines[0];
    const itemMatch = firstLine.match(/(.+?)@\s*(.+)/);
    let namepart = itemMatch ? itemMatch[1].trim() : firstLine.trim();
    poke.item = itemMatch ? itemMatch[2].trim().toLowerCase().replace(/\s+/g, '-') : '';
    
    // Extract a trailing gender marker "(M)"/"(F)" first, so it isn't mistaken
    // for a nickname/species group (e.g. "Pika (Pikachu) (M)").
    const genderMatch = namepart.match(/\s*\(([MF])\)\s*$/i);
    if (genderMatch) {
      poke.gender = genderMatch[1].toUpperCase() === 'M' ? 'male' : 'female';
      namepart = namepart.replace(genderMatch[0], '').trim();
    }

    // Check for nickname: "Nickname (Species)"
    const nicknameMatch = namepart.match(/(.+?)\s*\(([^)]+)\)/);
    if (nicknameMatch) {
      poke.nickname = nicknameMatch[1].trim();
      poke.name = nicknameMatch[2].trim().toLowerCase().replace(/\s+/g, '-');
    } else {
      poke.name = namepart.toLowerCase().replace(/\s+/g, '-');
    }
    
    // Parse other lines
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      
      if (line.startsWith('Ability:')) {
        poke.ability = line.replace('Ability:', '').trim().toLowerCase().replace(/\s+/g, '-');
      } else if (line.startsWith('Level:')) {
        poke.level = parseInt(line.replace('Level:', '').trim()) || 100;
      } else if (line.startsWith('Shiny:')) {
        poke.shiny = line.toLowerCase().includes('yes');
      } else if (line.startsWith('Tera Type:')) {
        poke.teraType = line.replace('Tera Type:', '').trim().toLowerCase();
      } else if (line.startsWith('EVs:')) {
        const evStr = line.replace('EVs:', '').trim();
        const evParts = evStr.split('/').map(p => p.trim());
        evParts.forEach(part => {
          const match = part.match(/(\d+)\s*(HP|Atk|Def|SpA|SpD|Spe)/i);
          if (match) {
            const stat = { hp: 'hp', atk: 'atk', def: 'def', spa: 'spa', spd: 'spd', spe: 'spe' }[match[2].toLowerCase()];
            if (stat) poke.evs[stat] = parseInt(match[1]);
          }
        });
      } else if (line.startsWith('IVs:')) {
        const ivStr = line.replace('IVs:', '').trim();
        const ivParts = ivStr.split('/').map(p => p.trim());
        ivParts.forEach(part => {
          const match = part.match(/(\d+)\s*(HP|Atk|Def|SpA|SpD|Spe)/i);
          if (match) {
            const stat = { hp: 'hp', atk: 'atk', def: 'def', spa: 'spa', spd: 'spd', spe: 'spe' }[match[2].toLowerCase()];
            if (stat) poke.ivs[stat] = parseInt(match[1]);
          }
        });
      } else if (line.endsWith('Nature')) {
        const nature = line.replace('Nature', '').trim().toLowerCase();
        if (NATURES[nature]) poke.nature = nature;
      } else if (line.startsWith('-')) {
        const move = line.replace('-', '').trim().toLowerCase().replace(/\s+/g, '-');
        if (poke.moves.length < 4) poke.moves.push(move);
      }
    }
    
    // Pad moves to 4
    while (poke.moves.length < 4) poke.moves.push('');
    
    pokemon.push(poke);
    if (pokemon.length >= 6) break;
  }
  
  return pokemon;
};
